import { useRef, useEffect, memo } from "react";
import { Editor } from "@tinymce/tinymce-react";

import "tinymce/tinymce";
import "tinymce/icons/default";
import "tinymce/themes/silver";
import "tinymce/models/dom";

import "tinymce/plugins/advlist";
import "tinymce/plugins/anchor";
import "tinymce/plugins/autolink";
import "tinymce/plugins/autosave";
import "tinymce/plugins/charmap";
import "tinymce/plugins/code";
import "tinymce/plugins/emoticons";
import "tinymce/plugins/fullscreen";
import "tinymce/plugins/help";
import "tinymce/plugins/image";
import "tinymce/plugins/insertdatetime";
import "tinymce/plugins/link";
import "tinymce/plugins/lists";
import "tinymce/plugins/media";
import "tinymce/plugins/preview";
import "tinymce/plugins/searchreplace";
import "tinymce/plugins/table";
import "tinymce/plugins/visualblocks";
import "tinymce/plugins/wordcount";
import "tinymce/plugins/nonbreaking";

const processContent = (html) => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  const tables = tempDiv.querySelectorAll("table");
  tables.forEach((table) => {
    table.style.borderCollapse = "collapse";

    const cells = table.querySelectorAll("td, th");
    cells.forEach((cell) => {
      cell.style.border = "1px solid #ccc";
      cell.style.verticalAlign = "top";

      if (table.classList.contains("medical-table")) {
        cell.style.padding = "1px 3px";
        cell.style.lineHeight = "0.85";
        cell.style.fontSize = "11px";
        cell.style.height = "14px";
        cell.style.whiteSpace = "nowrap";
      } else if (table.classList.contains("compact-table")) {
        cell.style.padding = "1px 4px";
        cell.style.lineHeight = "0.9";
        cell.style.fontSize = "11px";
        cell.style.height = "16px";
      } else {
        cell.style.padding = "2px 6px";
        cell.style.lineHeight = "1";
        cell.style.fontSize = "13px";
      }

      const paragraphs = cell.querySelectorAll("p");
      paragraphs.forEach((p) => {
        p.style.margin = "0";
        p.style.padding = "0";
        p.style.lineHeight = "inherit";
      });
    });
  });

  return tempDiv.innerHTML;
};

const TextEditor = ({ editorValue, height, setEditorValue }) => {
  const editorRef = useRef(null);

  const timerRef = useRef(null);
  const setValueRef = useRef(setEditorValue);

  useEffect(() => {
    setValueRef.current = setEditorValue;
  }, [setEditorValue]);

  const flush = () => {
    clearTimeout(timerRef.current);
    if (editorRef.current) {
      setValueRef.current(processContent(editorRef.current.getContent()));
    }
  };

  const handleEditorChange = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, 400);
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return (
    <Editor
      onInit={(_evt, editor) => (editorRef.current = editor)}
      initialValue={editorValue}
      onEditorChange={handleEditorChange}
      onBlur={flush} 
      init={{
        height: height || 400,
        menubar: true,
        branding: false,
        toolbar_mode: "sliding",
        promotion: false,
        plugins: [
          "advlist",
          "anchor",
          "autolink",
          "autosave",
          "charmap",
          "code",
          "emoticons",
          "fullscreen",
          "help",
          "image",
          "insertdatetime",
          "link",
          "lists",
          "media",
          "preview",
          "searchreplace",
          "table",
          "visualblocks",
          "wordcount",
          "nonbreaking",
          "paste",
        ],
        toolbar:
          "undo redo | fontfamily fontsize lineheight | bold italic underline strikethrough | " +
          "forecolor backcolor | alignleft aligncenter alignright alignjustify | " +
          "bullist numlist outdent indent | link image media emoticons charmap | " +
          "insertdatetime table compacttable makecompact tablerowheader tablecellprops tableprops | paste pastetext | code fullscreen preview | searchreplace | help",

        table_toolbar:
          "tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol | tablecellprops",
        table_class_list: [
          { title: "Default", value: "" },
          { title: "Compact Table", value: "compact-table" },
          { title: "Medical/Data Table", value: "medical-table" },
          { title: "Bordered Table", value: "bordered-table" },
        ],
        table_cell_class_list: [
          { title: "None", value: "" },
          { title: "Small Cell", value: "small-cell" },
          { title: "Compact Cell", value: "compact-cell" },
        ],
        table_row_class_list: [
          { title: "None", value: "" },
          { title: "Small Row", value: "small-row" },
          { title: "Compact Row", value: "compact-row" },
        ],

        content_style: `
          body { 
            font-family: Helvetica, Arial, sans-serif; 
            font-size: 14px;
            line-height: 1.4;
          }

          body.images-hidden img {
            display: none !important;
          }
          
          /* Force table line height controls - IMPORTANT */
          table {
            border-collapse: collapse !important;
            line-height: 1 !important;
          }
          
          table td, table th {
            border: 1px solid #ccc !important;
            padding: 2px 6px !important;
            vertical-align: top !important;
            line-height: 1 !important;
            height: auto !important;
            min-height: 18px !important;
            font-size: 13px !important;
          }
          
          /* Override any default spacing */
          table p {
            margin: 0 !important;
            padding: 0 !important;
            line-height: 1 !important;
          }
          
          table br {
            line-height: 1 !important;
          }
          
          /* Compact table styles */
          table.compact-table {
            line-height: 0.9 !important;
          }
          
          table.compact-table td,
          table.compact-table th {
            padding: 1px 4px !important;
            line-height: 0.9 !important;
            font-size: 11px !important;
            height: 16px !important;
          }
          
          /* Ultra compact for medical tables */
          table.medical-table td,
          table.medical-table th {
            padding: 1px 3px !important;
            line-height: 0.85 !important;
            font-size: 11px !important;
            height: 14px !important;
            white-space: nowrap !important;
          }
          
          /* Remove extra spacing from table content */
          table * {
            line-height: inherit !important;
          }
          
          /* Specific fixes for TinyMCE default behaviors */
          .mce-content-body table td,
          .mce-content-body table th {
            line-height: 1 !important;
            padding: 2px 4px !important;
          }
          
          /* Force compact on all tables by default */
          table:not([class]) td,
          table:not([class]) th {
            padding: 2px 4px !important;
            line-height: 1 !important;
            height: auto !important;
          }
        `,

        extended_valid_elements: "table[*],tr[*],td[*],th[*]",
        valid_styles: {
          "*": "border,border-collapse,padding,margin,line-height,height,font-size,vertical-align,white-space,width",
        },
        inline_styles: true,
        force_p_newlines: false,

        setup: function (editor) {
          editor.ui.registry.addButton("compacttable", {
            text: "Medical Table",
            onAction: function () {
              editor.execCommand("mceInsertTable", false, {
                rows: 3,
                cols: 2,
                options: {
                  headerRows: 1,
                  headerCols: 0,
                },
              });

              setTimeout(() => {
                const table = editor.selection.getNode().closest("table");
                if (table) {
                  editor.dom.addClass(table, "medical-table");
                  const cells = table.querySelectorAll("td, th");
                  cells.forEach((cell) => {
                    cell.style.lineHeight = "0.85";
                    cell.style.padding = "1px 3px";
                    cell.style.fontSize = "11px";
                    cell.style.border = "1px solid #ccc";
                    cell.style.verticalAlign = "top";
                  });
                  // Trigger change event
                  editor.fire("change");
                }
              }, 100);
            },
          });

          editor.ui.registry.addButton("makecompact", {
            text: "Make Compact",
            onAction: function () {
              const table = editor.selection.getNode().closest("table");
              if (table) {
                editor.dom.addClass(table, "medical-table");
                const cells = table.querySelectorAll("td, th");
                cells.forEach((cell) => {
                  cell.style.lineHeight = "0.85";
                  cell.style.padding = "1px 3px";
                  cell.style.fontSize = "11px";
                  cell.style.height = "14px";
                  cell.style.border = "1px solid #ccc";
                  cell.style.verticalAlign = "top";
                });
                // Trigger change event
                editor.fire("change");
              }
            },
          });

          editor.on("init", function () {
            setTimeout(() => {
              const tables = editor.getDoc().querySelectorAll("table");
              tables.forEach((table) => {
                table.style.borderCollapse = "collapse";
                const cells = table.querySelectorAll("td, th");
                cells.forEach((cell) => {
                  if (!cell.style.border) {
                    cell.style.lineHeight = "1";
                    cell.style.padding = "2px 4px";
                    cell.style.border = "1px solid #ccc";
                    cell.style.verticalAlign = "top";
                  }
                });
              });
              // Trigger change to process content
              editor.fire("change");
            }, 500);
          });

          // Handle all table creation events
          editor.on("NewTable", function () {
            setTimeout(() => {
              const table = editor.selection.getNode().closest("table");
              if (table) {
                table.style.borderCollapse = "collapse";
                const cells = table.querySelectorAll("td, th");
                cells.forEach((cell) => {
                  cell.style.border = "1px solid #ccc";
                  cell.style.padding = "2px 6px";
                  cell.style.verticalAlign = "top";
                  cell.style.lineHeight = "1";
                  cell.style.fontSize = "13px";
                });
                editor.fire("change");
              }
            }, 100);
          });

          // Monitor content changes and apply styles
          editor.on("SetContent", function () {
            setTimeout(() => {
              const tables = editor.getDoc().querySelectorAll("table");
              tables.forEach((table) => {
                if (!table.style.borderCollapse) {
                  table.style.borderCollapse = "collapse";
                }
                const cells = table.querySelectorAll("td, th");
                cells.forEach((cell) => {
                  if (!cell.style.border) {
                    cell.style.border = "1px solid #ccc";
                    cell.style.padding = "2px 6px";
                    cell.style.verticalAlign = "top";
                    cell.style.lineHeight = "1";
                    cell.style.fontSize = "13px";
                  }
                });
              });
            }, 100);
          });

          editor.on("init", function () {
            const menubar = editor.getContainer().querySelector(".tox-menubar");
            if (!menubar) return;

            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = "tox-mbtn";
            btn.style.marginLeft = "auto";
            btn.style.cursor = "pointer";

            const refresh = () => {
              const body = editor.getBody();
              const hasImages = editor.dom.select("img").length > 0;
              const hidden = editor.dom.hasClass(body, "images-hidden");

              if (!hasImages && hidden) {
                editor.dom.removeClass(body, "images-hidden");
              }
              btn.disabled = !hasImages;
              btn.style.opacity = hasImages ? "1" : "0.4";
              btn.textContent =
                hasImages && editor.dom.hasClass(body, "images-hidden")
                  ? "Show Images"
                  : "Hide Images";
            };

            btn.addEventListener("click", function () {
              editor.dom.toggleClass(editor.getBody(), "images-hidden");
              refresh();
            });

           const container = editor.getContainer();
          container.style.position = "relative";

          btn.className = "";
          btn.style.position = "absolute";
          btn.style.top = "6px";
          btn.style.right = "10px";
          btn.style.zIndex = "5";
          btn.style.whiteSpace = "nowrap";
          btn.style.padding = "3px 10px";
          btn.style.fontSize = "13px";
          btn.style.border = "1px solid #ccc";
          btn.style.borderRadius = "4px";
          btn.style.background = "#fff";
          btn.style.cursor = "pointer";

          container.appendChild(btn);
            refresh();
            editor.on("NodeChange SetContent Undo Redo KeyUp", refresh);
          });
        },
      }}
    />
  );
};

export default memo(TextEditor, () => true);
